<script lang="ts">
  import { useForm, usePage } from '@inertiajs/svelte'; 
  import { Link } from '@inertiajs/svelte';
  import { Button } from '$lib/components/ui/button';
  import { Card, CardContent, CardHeader, CardTitle } from '$lib/components/ui/card';
  import { Input } from '$lib/components/ui/input';
  import { Label } from '$lib/components/ui/label';
  import { inertia } from '@inertiajs/svelte';
  import { CornerDownLeft } from 'lucide-svelte'; 

  import Turnstile from '$lib/components/Turnstile.svelte';
  import BindPhoneModal from '$lib/components/BindPhoneModal.svelte';
  import logoGreen from '../../assets/logo/logo-green.svg'
  import fentimeWhite from '../../assets/logo/fentime-w.svg'

  const page = usePage();
  const turnstileConfig = page.props.turnstile || { enabled: false, site_key: '' };

  const form = useForm({
    email_address: '',
    password: '',
    'cf-turnstile-response': ''
  });

  const errors = $derived(page.props.errors || {});
  const flash = $derived(page.props.flash || {});
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.login || {});
  const legal = $derived(translations.legal || {});
  const tApp =  $derived(translations.app || {});

  let siteRegion = $derived((page.props.site_region as any) || 'US');

  //  Twitter Card 与 Open Graph 基础配置
  const metaTitle = "Fentime Aurora | Build High-Performance Websites in Minutes";
  const metaDescription = "Deploy multi-tenant, ultra-fast websites effortlessly. Fentime Aurora combines lightning-fast SSR rendering, built-in edge analytics, and zero-overhead CMS architecture.";
  // 建议准备一张 1200x630 的 Banner 图放在 static 静态资源目录下
  const metaImage = "https://cdn.sites.fentime.cn/zdakb8ljq1ydsyfw0fjcwm6d67bp"; 
  const canonicalUrl = "https://aurora.fentime.com/login";

  
  let portalUrl = $derived(siteRegion === 'CHINA' ? 'https://www.fentime.cn' : 'https://www.fentime.com');

  const getCsrfToken = () => {
    if (typeof document !== 'undefined') {
      return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    }
    return '';
  };

  function handleVerify(token) {
    form['cf-turnstile-response'] = token;
  }

  function submit(e: Event) {
    e.preventDefault(); 
    form.post('/login', {
      onError: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  let isSubmitDisabled = $derived(
    form.processing || (turnstileConfig.enabled && !form['cf-turnstile-response'])
  );
</script>

<svelte:head>
  <!-- 基础 SEO -->
  <title>{t.title ? `${t.title} - ${tApp.site_name}` : 'Sign In - Fentime Aurora'}</title>
  <meta name="description" content={metaDescription} />
  <link rel="canonical" href={canonicalUrl} />

  <!-- Open Graph (微信、FB、LinkedIn 兜底) -->
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Fentime Aurora" />
  <meta property="og:title" content={metaTitle} />
  <meta property="og:description" content={metaDescription} />
  <meta property="og:image" content={metaImage} />
  <meta property="og:url" content={canonicalUrl} />

  <!-- Twitter (X) Card Meta Tags -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:site" content="@fentime_com" />
  <meta name="twitter:creator" content="@fentime_com" />
  <meta name="twitter:title" content={metaTitle} />
  <meta name="twitter:description" content={metaDescription} />
  <meta name="twitter:image" content={metaImage} />
  <meta name="twitter:image:alt" content="Fentime Aurora - Modern Website Builder Engine" />
</svelte:head>

<div class="flex min-h-[calc(100vh)]">
  <div class="hidden md:flex md:w-1/2 bg-neutral-900 text-white p-8 items-center justify-center relative">
  
    <!-- 💡 左上角的 Logo + 字符区域 -->
    <div class="absolute top-8 left-8 flex items-center gap-2 select-none">
      <img src={logoGreen} alt="Fentime Logo" class="h-10 w-10 animate-in fade-in zoom-in-95 duration-700 ease-out" />
      <img src={fentimeWhite} alt="Fentime Logo" class="h-5 w-auto animate-in fade-in slide-in-from-left-2 duration-700 delay-1850 ease-out fill-mode-backwards" />
    </div>

    <!-- 原有的中部居中内容保持不变 -->
    <div class="w-full">
      <h2 class="text-4xl font-bold mb-4 tracking-tight">{t.brand_title}</h2>
      <p class="text-gray-400 text-lg">
        {t.brand_subtitle}
      </p>
    </div>
  </div>

  <div class="w-full md:w-1/2 p-6 flex md:items-center justify-center bg-zinc-50/50">
    <div class="w-full max-w-sm space-y-8">
      
      {#if flash?.notice}
        <div class="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-md text-sm">
          {flash.notice}
        </div>
      {/if}
      
      {#if flash?.alert}
        <div class="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
          {flash.alert}
        </div>
      {/if}

      <Card class="shadow-xl bg-white">
        <CardHeader>
          <CardTitle class="text-center text-2xl font-bold">{t.card_title}</CardTitle>
        </CardHeader>
        <CardContent class="pt-6 space-y-6">
          <!--  1. 第三方快捷登录区域（根据 siteRegion 动态切换） -->
          <div class="space-y-3">
            {#if siteRegion === 'CHINA'}
              <!-- 微信登录按钮 -->
              <form action="/auth/wechat" method="post">
                <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                <Button variant="outline" type="submit" class="w-full flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
                  
                  <svg width="800px" height="800px" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">

                    <path fill="#2DC100" d="M300 255c0 24.854-20.147 45-45 45H45c-24.854 0-45-20.146-45-45V45C0 20.147 20.147 0 45 0h210c24.853 0 45 20.147 45 45v210z"/>

                    <g fill="#FFF">

                    <path d="M200.803 111.88c-24.213 1.265-45.268 8.605-62.362 25.188-17.271 16.754-25.155 37.284-23 62.734-9.464-1.172-18.084-2.462-26.753-3.192-2.994-.252-6.547.106-9.083 1.537-8.418 4.75-16.488 10.113-26.053 16.092 1.755-7.938 2.891-14.889 4.902-21.575 1.479-4.914.794-7.649-3.733-10.849-29.066-20.521-41.318-51.232-32.149-82.85 8.483-29.25 29.315-46.989 57.621-56.236 38.635-12.62 82.054.253 105.547 30.927 8.485 11.08 13.688 23.516 15.063 38.224zm-111.437-9.852c.223-5.783-4.788-10.993-10.74-11.167-6.094-.179-11.106 4.478-11.284 10.483-.18 6.086 4.475 10.963 10.613 11.119 6.085.154 11.186-4.509 11.411-10.435zm58.141-11.171c-5.974.11-11.022 5.198-10.916 11.004.109 6.018 5.061 10.726 11.204 10.652 6.159-.074 10.83-4.832 10.772-10.977-.051-6.032-4.981-10.79-11.06-10.679z"/>

                    <path d="M255.201 262.83c-7.667-3.414-14.7-8.536-22.188-9.318-7.459-.779-15.3 3.524-23.104 4.322-23.771 2.432-45.067-4.193-62.627-20.432-33.397-30.89-28.625-78.254 10.014-103.568 34.341-22.498 84.704-14.998 108.916 16.219 21.129 27.24 18.646 63.4-7.148 86.284-7.464 6.623-10.15 12.073-5.361 20.804.884 1.612.985 3.653 1.498 5.689zm-87.274-84.499c4.881.005 8.9-3.815 9.085-8.636.195-5.104-3.91-9.385-9.021-9.406-5.06-.023-9.299 4.318-9.123 9.346.166 4.804 4.213 8.69 9.059 8.696zm56.261-18.022c-4.736-.033-8.76 3.844-8.953 8.629-.205 5.117 3.772 9.319 8.836 9.332 4.898.016 8.768-3.688 8.946-8.562.19-5.129-3.789-9.364-8.829-9.399z"/>

                    </g>

                    </svg>
                  
                  <span class="font-medium text-sm">{t.wechat_login || '微信一键登录'}</span>
                </Button>
              </form>
            {:else}
              <!-- Google 登录按钮 -->
              <form action="/auth/google_oauth2" method="post">
                <input type="hidden" name="authenticity_token" value={getCsrfToken()} />
                <Button variant="outline" type="submit" class="w-full flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
                  <!-- Google Icon SVG -->
                  <svg class="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                  </svg>
                  <span class="font-medium text-sm">{t.google_login || 'Continue with Google'}</span>
                </Button>
              </form>
            {/if}
          </div>

          <!--  2. 分割线："或使用邮箱登录" -->
          <div class="relative my-4">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-border"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-white px-2 text-muted-foreground">{t.or_email || '或使用邮箱登录'}</span>
            </div>
          </div>

          <!-- 3. 原有的账号密码表单 -->
          <form onsubmit={submit} class="space-y-5">
            <div class="space-y-2">
              <Label for="email_address">{t.email_label}</Label>
              <Input
                id="email_address"
                type="email" 
                placeholder="name@example.com"
                bind:value={form.email_address}
                required
                class={errors.email_address ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {#if errors.email_address}
                <p class="text-sm text-destructive">{errors.email_address}</p>
              {/if}
            </div>

            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <Label for="password">{t.password_label}</Label>
                <Link
                  href="/passwords/new"
                  class="text-sm text-primary hover:underline font-medium"
                >
                  {t.forgot_password}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder={t.password_placeholder}
                required
                bind:value={form.password}
                class={errors.password ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {#if errors.password}
                <p class="text-sm text-destructive">{errors.password}</p>
              {/if}
            </div>

            <!-- Turnstile 验证码组件 -->
            <Turnstile 
            enabled={turnstileConfig.enabled} 
            siteKey={turnstileConfig.site_key} 
            onVerify={handleVerify} />

            <Button
              type="submit"
              class="w-full"
              disabled={isSubmitDisabled}
            >
              {form.processing ? t.verifying : t.submit_btn}
            </Button>
          </form>

          <!-- 4. 底部注册链接分割线 -->
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-border"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-white px-2 text-muted-foreground">{t.no_account}</span>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <Button variant="outline" class="w-full p-0">
              <a href="/register" use:inertia class="w-full h-full flex items-center justify-center">
                {t.register_now}
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
      <div class="text-center text-xs text-muted-foreground mt-4 leading-relaxed">
        {legal.agreement_tip}
        <a href="{portalUrl}/terms" target="_blank" class="underline underline-offset-4 hover:text-primary transition-colors">
          {legal.terms_link}
        </a>
        {legal.and}
        <a href="{portalUrl}/privacy" target="_blank" class="underline underline-offset-4 hover:text-primary transition-colors">
          {legal.privacy_link}
        </a>
      </div>
    </div>
  </div>
</div>